import React, { useState, useCallback, useMemo } from "react";
import styled from "styled-components";
import { useQueryClient } from "react-query";
import { getText } from "oolib";
import { buildDocumentsGroupedOptions } from "@okf/ootils";
import { useGetDataLazy } from "@/utils/react-query-hooks/getData";
import { fetchFilterOptionsConfigs } from "../../../configs";
import { DrilldownList, SearchActionsToolbar } from "@/components/UI/DrilldownList";
import { DateRangeType, NumberRangeType } from "@/components/inputs/AccordionMultiSelect/comps/OptionsLightbox";
import { globalScrollbarStyling } from "oolib/dist/v2/themes/globalStyles";
import { GroupedStaticOptionsPanel } from "./GroupedStaticOptionsPanel";
import { NumberRangeCompactInput, HistogramButton } from "@/components/inputs/NumberRangeSlider/NumberRangeCompactInput";
import { Lightbox, LightboxContent, LightboxTrigger } from "@/components/UI/lightbox";
import { useOptionsLevel } from "./useOptionsLevel";

const StyledOuterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
`;

const StyledPanelWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
  overflow: auto;
  margin-top: 4px;
  ${globalScrollbarStyling}
`;

/**
 * Renders and fetches the options for one filter config. Routes to the right UI:
 * - grouped static options (Document Field) → GroupedStaticOptionsPanel
 * - everything else → FlatFilterOptionsPanel, which itself dispatches on the
 *   resolved comp type (date range / number range / multiselect-with-drilldown).
 */
export const FilterOptionsPanel = (props) => {
  // groupedOptions lives at the top level of the filter config (not inside `source`)
  // because `source` is propagated into activeFilters → request URLs.
  const groupedOptions = props.filterConfig?.groupedOptions;
  if (
    props.filterConfig?.source?.filterType === "staticType" &&
    Array.isArray(groupedOptions) &&
    groupedOptions.length > 0
  ) {
    return (
      <GroupedStaticOptionsPanel
        filterConfig={props.filterConfig}
        groupedOptions={groupedOptions}
        value={props.value || []}
        onChange={props.onChange}
      />
    );
  }
  if (props.filterConfig?.source?.filterType === "documentsType") {
    return <DocumentsByDatasetPanel {...props} />;
  }
  return <FlatFilterOptionsPanel {...props} />;
};

/**
 * Documents filter, grouped by dataset. Fetches every document title up front,
 * groups them via ootils, and reuses the Document Field panel (dataset folders → documents).
 */
const DocumentsByDatasetPanel = ({ filterConfig, value = [], onChange, contentTypes = [] }) => {
  const allTpls = useQueryClient().getQueryData("allTpls") || [];
  const datasets = filterConfig.source?.contentTypes || contentTypes;

  // Loads all documents at once; switch to a per-dataset lazy fetch if datasets get large
  const { data, status } = useGetDataLazy(
    ...fetchFilterOptionsConfigs.documentsType.getArgs({
      filterConfig,
      contentTypes,
      enableDataFetch: true,
      disableLimit: true,
    })
  );

  const groupedOptions = useMemo(
    () =>
      buildDocumentsGroupedOptions({
        docs: data?.pages?.flatMap((p) => (Array.isArray(p) ? p : p?.data || [])) || [],
        contentTypes: datasets,
        allTpls,
        getDocDisplay: (d) => getText(d.main?.title),
      }),
    [data, datasets, allTpls]
  );

  if (status === "loading") return <DrilldownList options={[]} isLoading />;

  return (
    <GroupedStaticOptionsPanel
      filterConfig={filterConfig}
      groupedOptions={groupedOptions}
      value={value}
      onChange={onChange}
    />
  );
};

const FlatFilterOptionsPanel = ({
  filterConfig,
  value = [],
  onChange,
  contentTypes = [],
  activeFilters = [],
  // When true (sidebar applied-filter blocks), number ranges render as a compact
  // editor whose histogram button opens the full slider in a popout. When false
  // (the Add Filter cascade, which is already a multilevel lightbox), the full
  // slider renders inline so we don't create a redundant extra level.
  compact = false,
  // Optional immediate child geo filter (e.g. State for Country). When present,
  // each option row drills into the child's values narrowed by the drilled-into
  // parent; committing writes to the child's OWN filterId (onChildChange).
  childFilterConfig,
  childValue = [],
  onChildChange,
}) => {
  const parentTagType = filterConfig?.source?.tagType;
  const enableDrilldown = !!childFilterConfig && !!parentTagType;
  const [drilledParent, setDrilledParent] = useState(null);

  // Child options fetch config: the child filter narrowed to the drilled-into parent
  // value (tags.<parentTag>.data._id ∈ [drilled]). Falls back to the parent config so
  // the child level's hook stays inert (no fetch) when there's no child filter.
  const childHookConfig = useMemo(() => {
    const base = childFilterConfig || filterConfig;
    return {
      ...base,
      source: {
        ...base.source,
        filterOptionsBy: undefined, // scope by the DRILLED value, not the applied parent filter
        findQuery: {
          ...(base.source?.findQuery || {}),
          ...(enableDrilldown && drilledParent
            ? { [`tags.${parentTagType}.data._id`]: { $in: [drilledParent.value] } }
            : {}),
        },
      },
    };
  }, [childFilterConfig, filterConfig, enableDrilldown, drilledParent, parentTagType]);

  // The two symmetric levels: parent list (with drill arrows) + drilled-in child.
  const parent = useOptionsLevel({
    filterConfig,
    value,
    onChange,
    contentTypes,
    activeFilters,
    initEnableDataFetch: true,
    hasChildren: enableDrilldown,
  });
  const child = useOptionsLevel({
    filterConfig: childHookConfig,
    value: childValue,
    onChange: onChildChange || (() => {}),
    contentTypes,
    activeFilters,
    initEnableDataFetch: false, // inert until a parent row is drilled
    hasChildren: false,
  });

  const getChildLevel = useCallback(
    () => ({
      options: child.options,
      value: child.selectedValue,
      isMulti: true,
      onChange: child.handleRowChange,
      isLoading: child.status === "loading",
      isFetchingNextPage: child.isFetchingNextPage,
      observerRef: child.hasNextPage ? child.observerRef : null,
      emptyMessage: `No ${childFilterConfig?.display || "options"}`,
      headerContent: <SearchActionsToolbar {...child.toolbarProps} />,
      showSelectedFirst: child.showSelectedFirst,
    }),
    [child, childFilterConfig]
  );

  const handleDrilldown = useCallback(
    (option) => {
      setDrilledParent(option);
      child.setEnableDataFetch();
    },
    [child]
  );

  // Non-multiselect filter types render their own editors instead of a list.
  if (parent.compType === "DateRangeType") {
    return <DateRangePanel filterConfig={filterConfig} value={value} onChange={onChange} />;
  }
  if (parent.compType === "NumberRangeType") {
    return (
      <NumberRangePanel
        filterConfig={filterConfig}
        value={value}
        onChange={onChange}
        compact={compact}
        lightboxConfig={parent.lightboxConfig}
        status={parent.status}
      />
    );
  }

  return (
    <StyledOuterWrapper>
      <SearchActionsToolbar {...parent.toolbarProps} />
      <StyledPanelWrapper>
        <DrilldownList
          options={parent.options}
          value={parent.selectedValue}
          isMulti={true}
          onChange={parent.handleRowChange}
          onDrilldown={enableDrilldown ? handleDrilldown : undefined}
          getChildLevel={enableDrilldown ? getChildLevel : undefined}
          isLoading={parent.status === "loading"}
          isFetchingNextPage={parent.isFetchingNextPage}
          observerRef={parent.hasNextPage ? parent.observerRef : null}
          emptyMessage={parent.search ? "No results found" : "No options available"}
          showSelectedFirst={parent.showSelectedFirst}
        />
      </StyledPanelWrapper>
    </StyledOuterWrapper>
  );
};

/** Date-range filter editor (reuses OptionsLightbox's DateRangeType). */
const DateRangePanel = ({ filterConfig, value, onChange }) => (
  <StyledPanelWrapper style={{ overflow: "visible", padding: "1rem" }}>
    <DateRangeType
      id={filterConfig.filterId}
      value={value}
      onChange={(_id, v) => onChange(v)}
    />
  </StyledPanelWrapper>
);

/**
 * Number-range filter editor. `compact` (sidebar) renders a compact input whose
 * histogram button pops out the full slider; otherwise the full slider renders
 * inline (the Add Filter cascade is already a multilevel lightbox).
 */
const NumberRangePanel = ({ filterConfig, value, onChange, compact, lightboxConfig, status }) => {
  const { min, max, step, aggregatedData, loading: propsLoading } = lightboxConfig?.props || {};
  // getLightboxProps flags `loading` on both an in-flight fetch AND a missing response
  // (`!data`), handing back placeholder bounds (0–100) with aggregatedData: null in that
  // case. Recomputing loading from `status` alone misses the `!data`/idle case, which
  // would render the slider against the placeholder (wrong bounds, no histogram bars).
  // Prefer the flag so the slider only ever renders against real, fetched bounds.
  const isLoading = propsLoading ?? (status === "loading" || status === "pending");

  if (compact) {
    return (
      <StyledPanelWrapper style={{ overflow: "visible", padding: "0.4rem 0" }}>
        <Lightbox placement="right-start">
          <NumberRangeCompactInput
            value={value || {}}
            onChange={onChange}
            min={min ?? 0}
            max={max ?? 100}
            // While bounds load (or the fetch failed) min/max are the 0–100 placeholder;
            // disable editing so typed values aren't clamped to it.
            disabled={isLoading}
            histogramButton={
              <LightboxTrigger asChild>
                <HistogramButton />
              </LightboxTrigger>
            }
          />
          <LightboxContent style={{ zIndex: 99999 }}>
            <div style={{ width: "300px", padding: "0 0 3px 0" }}>
              <NumberRangeType
                id={filterConfig.filterId}
                value={value}
                onChange={(_id, v) => onChange(v)}
                min={min}
                max={max}
                step={step}
                loading={isLoading}
                aggregatedData={aggregatedData}
                showModeToggle={false}
              />
            </div>
          </LightboxContent>
        </Lightbox>
      </StyledPanelWrapper>
    );
  }

  return (
    <StyledPanelWrapper style={{ overflow: "visible" }}>
      <NumberRangeType
        id={filterConfig.filterId}
        value={value}
        onChange={(_id, v) => onChange(v)}
        min={min}
        max={max}
        step={step}
        loading={isLoading}
        aggregatedData={aggregatedData}
      />
    </StyledPanelWrapper>
  );
};
